/**
 * V3.1m-phase2a — Page admin WhatsApp Meta Cloud API.
 *
 * ⚠ AUCUN secret ne doit JAMAIS s'afficher ici (access token, app secret,
 *   verify token, bearer, etc.).
 */
import { useEffect, useState } from 'react';
import {
  Activity, Send, RefreshCw, CheckCircle2, AlertTriangle, Phone,
  ArrowDownLeft, ArrowUpRight, Loader2, Clock,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import WhatsAppIdentitiesPanel from '@/components/WhatsAppIdentitiesPanel';


const STATUS_COLORS = {
  received: 'bg-slate-100 text-slate-700',
  queued: 'bg-blue-50 text-blue-700',
  sent: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-emerald-100 text-emerald-800',
  read: 'bg-emerald-200 text-emerald-900',
  failed: 'bg-rose-50 text-rose-700',
};

const PROC_COLORS = {
  new: 'bg-slate-100 text-slate-700',
  echoed: 'bg-emerald-50 text-emerald-700',
  rate_limited: 'bg-amber-50 text-amber-700',
  identity_unknown: 'bg-blue-50 text-blue-700',
  identity_disabled: 'bg-rose-50 text-rose-700',
  echo_failed: 'bg-rose-50 text-rose-700',
  sent_by_crm: 'bg-slate-100 text-slate-700',
};


export default function WhatsAppAdmin() {
  const [health, setHealth] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ direction: '', processing_status: '' });
  const [sendForm, setSendForm] = useState({ to: '', body: '' });
  const [sending, setSending] = useState(false);

  const loadHealth = async () => {
    try {
      const r = await api.get('/whatsapp/admin/health');
      setHealth(r.data);
    } catch (e) {
      toast.error('Erreur chargement health');
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (filters.direction) params.set('direction', filters.direction);
      if (filters.processing_status) params.set('processing_status', filters.processing_status);
      const r = await api.get(`/whatsapp/admin/messages?${params}`);
      setMessages(r.data?.items || []);
    } catch (e) {
      toast.error('Erreur chargement messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHealth(); loadMessages(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMessages(); /* eslint-disable-next-line */ }, [filters.direction, filters.processing_status]);

  const refreshAll = async () => {
    await Promise.all([loadHealth(), loadMessages()]);
    toast.success('Actualisé');
  };

  const handleSend = async () => {
    if (!sendForm.to || !sendForm.body) {
      toast.error('Numéro et message requis');
      return;
    }
    try {
      setSending(true);
      await api.post('/whatsapp/admin/send-test', sendForm);
      toast.success('Envoyé via Meta');
      setSendForm({ to: '', body: '' });
      loadMessages();
    } catch (e) {
      const detail = e?.response?.data;
      // FastAPI 400 → detail string ; service error → {message, meta_error_code}
      const msg = detail?.message
        || detail?.detail
        || (Array.isArray(detail?.detail)
            ? detail.detail.map(d => d.msg).join(' ; ')
            : null)
        || 'Erreur envoi';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
            WhatsApp — Intégration Meta
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Phase 2A — webhook, observabilité, test d'envoi (sans IA).
          </p>
        </div>
        <Button variant="outline" onClick={refreshAll} data-testid="wa-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Actualiser
        </Button>
      </div>

      {/* HEALTH */}
      {health && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3"
              data-testid="wa-health-cards">
          <HealthCard
            label="Config env" valid={health.env_configured}
            value={health.env_configured ? 'OK' : 'Incomplète'}
            testid="wa-health-env"
          />
          <HealthCard
            label="Echo mode" valid={!health.echo_mode}
            value={health.echo_mode ? 'ON (test)' : 'OFF'}
            testid="wa-health-echo"
          />
          <HealthCard
            label="Dernière réception"
            value={formatRelative(health.last_inbound_at) || '—'}
            testid="wa-health-last-in"
          />
          <HealthCard
            label="Dernier envoi"
            value={formatRelative(health.last_outbound_at) || '—'}
            testid="wa-health-last-out"
          />
          <StatCard
            label="Inbound 24h" value={health.inbound_24h}
            icon={<ArrowDownLeft className="w-4 h-4" />}
            testid="wa-stat-in"
          />
          <StatCard
            label="Outbound 24h" value={health.outbound_24h}
            icon={<ArrowUpRight className="w-4 h-4" />}
            testid="wa-stat-out"
          />
          <StatCard
            label="Erreurs 24h" value={health.errors_24h}
            icon={<AlertTriangle className="w-4 h-4" />}
            danger={health.errors_24h > 0}
            testid="wa-stat-err"
          />
          <StatCard
            label="Rate limit"
            value={`${health.rate_limit_per_hour}/h`}
            icon={<Clock className="w-4 h-4" />}
            testid="wa-stat-rl"
          />
        </div>
      )}

      {/* UTILISATEURS AUTORISÉS */}
      <WhatsAppIdentitiesPanel />

      {/* SEND TEST */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200"
            data-testid="wa-send-test-form">
        <h2 className="text-base font-semibold text-slate-900 mb-3"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
          Envoyer un message test
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Numéro (E.164)</Label>
            <Input
              placeholder="+33612345678" value={sendForm.to}
              onChange={e => setSendForm({ ...sendForm, to: e.target.value })}
              data-testid="wa-send-to"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={2} value={sendForm.body}
              onChange={e => setSendForm({ ...sendForm, body: e.target.value })}
              data-testid="wa-send-body"
            />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={handleSend} disabled={sending}
                   data-testid="wa-send-submit">
            {sending
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Envoi…</>
              : <><Send className="w-4 h-4 mr-1.5" /> Envoyer test</>}
          </Button>
        </div>
      </div>

      {/* MESSAGES TABLE */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
            Messages ({messages.length})
          </h2>
          <div className="flex gap-2">
            <Select value={filters.direction || '__all__'}
                     onValueChange={v => setFilters(f => ({
                       ...f, direction: v === '__all__' ? '' : v,
                     }))}>
              <SelectTrigger className="w-36" data-testid="wa-filter-direction">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes directions</SelectItem>
                <SelectItem value="inbound">Entrants</SelectItem>
                <SelectItem value="outbound">Sortants</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.processing_status || '__all__'}
                     onValueChange={v => setFilters(f => ({
                       ...f, processing_status: v === '__all__' ? '' : v,
                     }))}>
              <SelectTrigger className="w-44" data-testid="wa-filter-proc">
                <SelectValue placeholder="Processing status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous statuts</SelectItem>
                <SelectItem value="new">new</SelectItem>
                <SelectItem value="echoed">echoed</SelectItem>
                <SelectItem value="rate_limited">rate_limited</SelectItem>
                <SelectItem value="identity_unknown">identity_unknown</SelectItem>
                <SelectItem value="identity_disabled">identity_disabled</SelectItem>
                <SelectItem value="sent_by_crm">sent_by_crm</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : messages.length === 0 ? (
          <p className="p-4 text-sm text-slate-500 italic">
            Aucun message. Les webhooks Meta arriveront ici dès la configuration terminée.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left p-2">Dir.</th>
                  <th className="text-left p-2">Numéro</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Contenu</th>
                  <th className="text-left p-2">Identité</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Processing</th>
                  <th className="text-left p-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {messages.map(m => (
                  <tr
                    key={m.message_id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`wa-msg-row-${m.message_id}`}
                  >
                    <td className="p-2">
                      {m.direction === 'inbound'
                        ? <ArrowDownLeft className="w-4 h-4 text-blue-600" />
                        : <ArrowUpRight className="w-4 h-4 text-emerald-600" />}
                    </td>
                    <td className="p-2 font-mono text-xs">
                      {m.direction === 'inbound' ? m.from_number : m.to_number}
                    </td>
                    <td className="p-2">
                      <Badge variant="outline" className="text-[10px]">
                        {m.message_type}
                      </Badge>
                    </td>
                    <td className="p-2 max-w-xs truncate">
                      {m.text || (m.media ? `${m.media.filename || m.media.media_id || 'media'}` : '—')}
                    </td>
                    <td className="p-2 text-xs">
                      {m.identity_status === 'known' && (
                        <Badge variant="outline"
                          className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          Connu
                        </Badge>
                      )}
                      {m.identity_status === 'disabled' && (
                        <Badge variant="outline"
                          className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                          Désactivé
                        </Badge>
                      )}
                      {m.identity_status === 'unknown' && (
                        <Badge variant="outline"
                          className="text-[10px] bg-slate-100 text-slate-600">
                          Inconnu
                        </Badge>
                      )}
                      {!m.identity_status && m.direction === 'outbound' && (
                        <Badge variant="outline"
                          className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                          CRM
                        </Badge>
                      )}
                    </td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${STATUS_COLORS[m.status] || 'bg-slate-100'}`}
                      >
                        {m.status}
                      </Badge>
                    </td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${PROC_COLORS[m.processing_status] || 'bg-slate-100'}`}
                      >
                        {m.processing_status || '—'}
                      </Badge>
                    </td>
                    <td className="p-2 text-xs text-slate-500">
                      {formatDate(m.received_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function HealthCard({ label, value, valid, testid }) {
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-slate-200"
          data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        {valid === true && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
        {valid === false && <AlertTriangle className="w-4 h-4 text-amber-600" />}
        <span className="text-sm font-semibold text-slate-900">{value}</span>
      </div>
    </div>
  );
}


function StatCard({ label, value, icon, danger, testid }) {
  return (
    <div className={`rounded-lg p-3 shadow-sm border ${
      danger ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'
    }`}
          data-testid={testid}>
      <div className="text-xs text-slate-500 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}


function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}


function formatRelative(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    return d.toLocaleDateString('fr-FR');
  } catch { return iso; }
}
